import React from 'react';

import { FormattedMessage, injectIntl, InjectedIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import Modal, {
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '@atlaskit/modal-dialog';
import Panel from '@atlaskit/panel';

import {
  closeBulkDeleteBranchesDialog,
  bulkDeleteBranches,
  getBulkDeleteBranchesSlice,
} from 'src/redux/branches';
import {
  BulkDeleteBranchesState,
  LoadingStatus,
} from 'src/redux/branches/reducers/bulk-delete-branches-reducer';
import { BucketState } from 'src/types/state';
import { publishTrackEvent } from 'src/utils/analytics/publish';

import { DetailedBranch } from '../../types';

import messages from './bulk-delete-branches-dialog.i18n';
import * as styles from './bulk-delete-branches-dialog.styled';

type Props = {
  intl: InjectedIntl;
};

const sortFn = (
  { name: aName }: DetailedBranch,
  { name: bName }: DetailedBranch
) => {
  if (aName < bName) {
    return -1;
  }
  if (aName > bName) {
    return 1;
  }
  return 0;
};

export const BulkDeleteBranchesDialog = injectIntl(({ intl }: Props) => {
  const dispatch = useDispatch();
  const {
    isBulkDeleteBranchesDialogOpen,
    bulkDeleteBranchesStatus,
    selectedBranches,
    stale,
  } = useSelector<BucketState, BulkDeleteBranchesState>(
    getBulkDeleteBranchesSlice
  );

  if (!isBulkDeleteBranchesDialogOpen) {
    return null;
  }

  return (
    <Modal
      onClose={() => dispatch(closeBulkDeleteBranchesDialog())}
      width="medium"
    >
      <ModalHeader>
        <ModalTitle appearance="danger">
          <FormattedMessage {...messages.dialogTitle} />
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        <FormattedMessage {...messages.dialogDescription} />
        <styles.PanelWrapper>
          <Panel
            header={intl.formatMessage(messages.showBranchesToBeDeletedHeader)}
          >
            {selectedBranches.sort(sortFn).map(br => (
              <styles.BranchLabel data-testid="branch-name" key={br.name}>
                <span>{br.name}</span>
              </styles.BranchLabel>
            ))}
          </Panel>
        </styles.PanelWrapper>
      </ModalBody>
      <ModalFooter>
        <CustomThemeButton
          appearance="danger"
          isDisabled={bulkDeleteBranchesStatus === LoadingStatus.Loading}
          isLoading={bulkDeleteBranchesStatus === LoadingStatus.Loading}
          onClick={() => {
            publishTrackEvent({
              action: 'deleted',
              actionSubject: 'branches',
              source: 'branchesScreen',
              attributes: {
                stale,
              },
            });
            dispatch(bulkDeleteBranches());
          }}
        >
          {intl.formatMessage(messages.deleteButton)}
        </CustomThemeButton>
        <CustomThemeButton
          appearance="subtle"
          isDisabled={bulkDeleteBranchesStatus === LoadingStatus.Loading}
          onClick={() => dispatch(closeBulkDeleteBranchesDialog())}
        >
          {intl.formatMessage(messages.cancelButton)}
        </CustomThemeButton>
      </ModalFooter>
    </Modal>
  );
});
